/**
 * Households API Hooks - Sophisticated pattern with TanStack Query
 *
 * Provides type-safe API operations for Households with proper error handling,
 * caching, and validation following the established architecture pattern.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query"

import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import type {
  HouseholdCreate,
  HouseholdCreateInput,
  HouseholdFilters,
  HouseholdListResponse,
  HouseholdResponse,
  HouseholdUpdate,
  HouseholdUpdateInput,
} from "@/types/household"
import { unwrapStandardResponse } from "@/utils/apiResponse"

import { useApiMutation } from "./useApiMutation"

/**
 * Fetch paginated list of households with permission checks
 */
export function useHouseholds(filters: HouseholdFilters = {}) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["households", filters],
    queryFn: async (): Promise<HouseholdListResponse> => {
      const response = await api.households.readHouseholdsApiV1HouseholdsGet({
        limit: filters.limit,
        skip: filters.skip,
        search: filters.search,
        sortBy: filters.sort_by,
        sortOrder: filters.sort_order,
        createdBy: filters.created_by,
        createdAfter: filters.created_after,
        createdBefore: filters.created_before,
        updatedAfter: filters.updated_after,
        updatedBefore: filters.updated_before,
      })

      return unwrapStandardResponse<HouseholdListResponse>(
        response,
      ) as HouseholdListResponse
    },
    enabled:
      hasPermission("households:read") || hasPermission("households:read_own"),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch single household by ID with permission checks
 */
export function useHousehold(householdId: string) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["households", householdId],
    queryFn: async (): Promise<HouseholdResponse> => {
      const response =
        await api.households.readHouseholdApiV1HouseholdsHouseholdIdGet({
          householdId: householdId,
        })

      return unwrapStandardResponse<HouseholdResponse>(
        response,
      ) as HouseholdResponse
    },
    enabled:
      !!householdId &&
      (hasPermission("households:read") ||
        hasPermission("households:read_own")),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Create new household with validation and error handling
 */
export function useCreateHousehold() {
  return useApiMutation<HouseholdResponse, unknown, HouseholdCreateInput>({
    mutationFn: async (householdData) => {
      // Let the backend handle permission checks

      const householdCreate: HouseholdCreate = {
        user_id: householdData.user_id ?? null,
        parent_first_name: householdData.parent_first_name,
        parent_last_name: householdData.parent_last_name,
        email: householdData.email,
        phone: householdData.phone ?? null,
        secondary_phone: householdData.secondary_phone ?? null,
        address: householdData.address ?? null,
        emergency_contact_name: householdData.emergency_contact_name ?? null,
        emergency_contact_phone: householdData.emergency_contact_phone ?? null,
        emergency_contact_relationship:
          householdData.emergency_contact_relationship ?? null,
        doctor_first_name: householdData.doctor_first_name ?? null,
        doctor_last_name: householdData.doctor_last_name ?? null,
        doctor_phone: householdData.doctor_phone ?? null,
        insurance_provider: householdData.insurance_provider ?? null,
        referral_source: householdData.referral_source ?? null,
        agreed_to_policy: householdData.agreed_to_policy ?? null,
        agreed_to_hold_harmless: householdData.agreed_to_hold_harmless ?? null,
        policy_agreement_date: householdData.policy_agreement_date ?? null,
        billing_email: householdData.billing_email ?? null,
        auto_pay_enabled: householdData.auto_pay_enabled ?? null,
        notes: householdData.notes ?? null,
        is_active: householdData.is_active ?? null,
      }

      const response = await api.households.createHouseholdApiV1HouseholdsPost({
        requestBody: householdCreate,
      })

      return unwrapStandardResponse<HouseholdResponse>(
        response,
      ) as HouseholdResponse
    },
    invalidateQueries: [["households"]],
    successToast: (data) => ({
      title: "Household created successfully",
      description: ` "${data.id}" has been created.`,
    }),
  })
}

/**
 * Update existing household with validation and error handling
 */
export function useUpdateHousehold() {
  return useApiMutation<
    HouseholdResponse,
    unknown,
    { householdId: string; householdData: HouseholdUpdateInput }
  >({
    mutationFn: async ({
      householdId,
      householdData,
    }: {
      householdId: string
      householdData: HouseholdUpdateInput
    }) => {
      // Let the backend handle permission checks

      const householdUpdate: HouseholdUpdate = {
        user_id: householdData.user_id,
        parent_first_name: householdData.parent_first_name,
        parent_last_name: householdData.parent_last_name,
        email: householdData.email,
        phone: householdData.phone,
        secondary_phone: householdData.secondary_phone,
        address: householdData.address,
        emergency_contact_name: householdData.emergency_contact_name,
        emergency_contact_phone: householdData.emergency_contact_phone,
        emergency_contact_relationship:
          householdData.emergency_contact_relationship,
        doctor_first_name: householdData.doctor_first_name,
        doctor_last_name: householdData.doctor_last_name,
        doctor_phone: householdData.doctor_phone,
        insurance_provider: householdData.insurance_provider,
        referral_source: householdData.referral_source,
        agreed_to_policy: householdData.agreed_to_policy,
        agreed_to_hold_harmless: householdData.agreed_to_hold_harmless,
        policy_agreement_date: householdData.policy_agreement_date,
        billing_email: householdData.billing_email,
        auto_pay_enabled: householdData.auto_pay_enabled,
        notes: householdData.notes,
        is_active: householdData.is_active,
      }

      const response =
        await api.households.updateHouseholdApiV1HouseholdsHouseholdIdPut({
          householdId: householdId,
          requestBody: householdUpdate,
        })

      return unwrapStandardResponse<HouseholdResponse>(
        response,
      ) as HouseholdResponse
    },
    setQueryData: [
      {
        queryKey: (variables) => ["households", variables.householdId],
        updater: (_previous, data) => data,
      },
    ],
    invalidateQueries: [["households"]],
  })
}

/**
 * Delete household with permission checks and error handling
 */
export function useDeleteHousehold() {
  return useApiMutation<{ householdId: string }, unknown, string>({
    mutationFn: async (householdId) => {
      // Let the backend handle permission checks
      await api.households.deleteHouseholdApiV1HouseholdsHouseholdIdDelete({
        householdId: householdId,
      })
      return { householdId }
    },
    removeQueries: [(variables) => ["households", variables]],
    invalidateQueries: [["households"]],
  })
}

/**
 * Invalidate households queries to force a refetch
 */
export function useInvalidateHouseholds() {
  const queryClient = useQueryClient()

  return () => {
    void queryClient.invalidateQueries({ queryKey: ["households"] })
  }
}
